import {
  ClockIcon,
  LightBulbIcon,
  MagnifyingGlassIcon,
  LifebuoyIcon,
} from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useAccount } from '../../hooks/useAccount';
import { useChangeSubscription, useCheckoutFlow } from '../../hooks/useAccountMutations';
import { useProducts } from '../../hooks/useProducts';
import { Product } from '../../services/models/product';
import {
  Subscription,
  SubscriptionInterval,
  SubscriptionStatus,
} from '../../services/models/subscription';
import { BillingRoute } from '../../utils/Routes';
import LoadingSpinner from '../loading-spinner/LoadingSpinner';
import ProductCard from '../product-card/ProductCard';

const newslitFeatures = [
  {
    id: 1,
    name: 'Save Time',
    description:
      'We do the research for you! You get a real-time dashboard and a daily news briefs via e-mail or Slack to ensure you never miss a thing.',
    icon: ClockIcon,
  },
  {
    id: 2,
    name: 'Cut the noise ',
    description:
      'We spot signals hidden to 90% of the world by tracking well over 100,000 news sources and integrating social signals from thousands of top business influencers.',
    icon: MagnifyingGlassIcon,
  },
  {
    id: 3,
    name: 'Stay ahead',
    description:
      'It’s easy to miss important stories. With Newslit, you can connect with over 2,000 applications via Zapier to create workflows and keep track of everything.',
    icon: LightBulbIcon,
  },
  {
    id: 4,
    name: 'Sit back and relax',
    description:
      'Newslit is working 24/7 so you can rest at ease. Did we mention? once is set up, is 100% automated.',
    icon: LifebuoyIcon,
  },
];

export default function Paywall() {
  const { account: currentAccount } = useAccount();
  const account = currentAccount!;
  const { changeSubscription } = useChangeSubscription();
  const { initCheckoutFlow } = useCheckoutFlow();
  const currentReportsCount = account?.metadata?.activeReportsCount || 0;
  const subscription = account.subscription;
  let isBilledMonthly = subscription?.interval !== SubscriptionInterval.Yearly;
  const { products } = useProducts();

  const [planRequiringConfirmation, setPlanRequiringConfirmation] = useState('');
  const [planRequiringLoading, setPlanRequiringLoading] = useState('');
  const [bestProduct, setBestProduct] = useState<Product>();

  useEffect(() => {
    // Compute best plan for user to upgrade
    const betterPlans = (products ?? [])
      .filter(p => p.published)
      .filter(p => {
        return p.features.briefs >= currentReportsCount;
      });

    setBestProduct(betterPlans[0]);
  }, [products, currentReportsCount]);

  const handleProductPriceSelection = (planId: string, product: Product) => {
    setPlanRequiringLoading(product.productId);

    if (account?.subscription?.planId && !account?.subscription?.canceled) {
      changeSubscription(planId);
      return;
    }

    initCheckoutFlow(planId);
  };

  const renderPlan = (product?: Product) => {
    if (!product) {
      return <LoadingSpinner />;
    }

    return (
      <ProductCard
        key={product.productId}
        account={account}
        active={false}
        mostPopular={false}
        product={product}
        isBilledMonthly={isBilledMonthly}
        showsConfirmation={planRequiringConfirmation === product.productId}
        showsLoading={planRequiringLoading === product.productId}
        handleConfirmationChange={(confirmation: boolean) => {
          if (confirmation) {
            setPlanRequiringConfirmation(product.productId);
          } else {
            setPlanRequiringConfirmation('');
          }
        }}
        onSelected={handleProductPriceSelection}
      />
    );
  };

  return (
    <div className="relative w-full bg-white">
      <div className="absolute inset-0" aria-hidden="true">
        <div className="absolute inset-y-0 right-0 w-1/2 bg-secondary-dark" />
      </div>
      <div className="relative max-w-7xl mx-auto lg:px-8 lg:grid lg:grid-cols-2">
        <div className="bg-white py-16 px-4 sm:py-12 sm:px-6 lg:px-0 lg:pr-8">
          <div className="max-w-lg mx-auto lg:mx-0">
            {subscription.status === SubscriptionStatus.TrialExpired && <HeaderTrialEnded />}

            {subscription.status === SubscriptionStatus.Trial && (
              <HeaderTrialing subscription={subscription} />
            )}

            <div className="lg:hidden mt-8 max-w-xs mx-auto w-full space-y-8 lg:mx-0">
              <div>
                <h2 className="sr-only">Price</h2>
                {renderPlan(bestProduct)}
                <NavLink to={BillingRoute} className="block mt-4 text-center text-base font-medium">
                  Explore all plans <span aria-hidden="true">&rarr;</span>
                </NavLink>
              </div>
            </div>

            <dl className="mt-12 space-y-10">
              {newslitFeatures.map(feature => (
                <div key={feature.name} className="relative">
                  <dt>
                    <div className="absolute h-12 w-12 flex items-center justify-center bg-secondary rounded-md">
                      <feature.icon className="h-6 w-6 text-white" aria-hidden="true" />
                    </div>
                    <p className="ml-16 text-lg leading-6 font-medium text-gray-900">
                      {feature.name}
                    </p>
                  </dt>
                  <dd className="mt-2 ml-16 text-base text-gray-500">{feature.description}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
        <div className="hidden lg:block bg-secondary-dark py-16 px-4 sm:py-24 sm:px-6 lg:bg-none lg:px-0 lg:pl-8 lg:flex lg:items-center lg:justify-end">
          <div className="max-w-lg mx-auto w-full space-y-8 lg:mx-0">
            <div className="px-6">
              <h2 className="sr-only">Price</h2>
              {renderPlan(bestProduct)}
            </div>
            <NavLink
              to={BillingRoute}
              className="block mt-4 text-center text-base font-medium text-indigo-200 hover:text-white"
            >
              Explore all plans <span aria-hidden="true">&rarr;</span>
            </NavLink>
          </div>
        </div>
      </div>
    </div>
  );
}

interface HeaderTrialingProps {
  subscription: Subscription;
}

const HeaderTrialing = ({ subscription }: HeaderTrialingProps) => {
  const trialDaysLeft = subscription.trialDaysLeft;

  return (
    <div>
      <h2 className="text-center text-2xl sm:text-3xl leading-8 font-extrabold tracking-tight text-gray-900">
        Enjoying your Newslit trial?
      </h2>
      <p className="mt-4 text-center text-xl text-gray-500">
        You have <span className="font-bold">{trialDaysLeft}</span> day
        {trialDaysLeft > 1 ? 's' : ''} left in your trial
      </p>
    </div>
  );
};

const HeaderTrialEnded = () => {
  return (
    <h2 className="text-center text-2xl sm:text-3xl leading-8 font-extrabold tracking-tight text-gray-900">
      Upgrade to keep using Newslit
    </h2>
  );
};
